import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { runCustomReport, getCustomReportFields } from '../lib/api';
import { ChartLine, Play, Download, FunnelSimple, Database } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ENTITY_OPTIONS = [
  'orders', 'movements', 'pick_lists', 'pack_tasks', 'shipping_labels',
  'receipts', 'adjustments', 'cycle_counts', 'skus', 'transfers',
];

export default function CustomReports() {
  const [entity, setEntity] = useState('orders');
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [filters, setFilters] = useState({ status: '', start_date: '', end_date: '' });
  const [limit, setLimit] = useState(500);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);

  useEffect(() => {
    setLoadingFields(true);
    setReport(null);
    setSelectedFields([]);
    getCustomReportFields(entity)
      .then(res => setAvailableFields(res.data.fields))
      .catch(() => toast.error('Failed to load fields'))
      .finally(() => setLoadingFields(false));
  }, [entity]);

  const toggleField = (f) => {
    setSelectedFields(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const res = await runCustomReport({
        entity,
        fields: selectedFields,
        filters: cleanFilters,
        limit: parseInt(limit),
      });
      setReport(res.data);
      toast.success(`Loaded ${res.data.returned} of ${res.data.total_matching} rows`);
    } catch (e) {
      toast.error('Report failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const exportCSV = () => {
    if (!report?.rows?.length) return;
    const keys = selectedFields.length ? selectedFields : Object.keys(report.rows[0]).filter(k => k !== 'id');
    const header = keys.join(',');
    const lines = report.rows.map(r => keys.map(k => {
      const v = r[k];
      if (v === null || v === undefined) return '';
      if (typeof v === 'object') return `"${JSON.stringify(v).replace(/"/g, '""')}"`;
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entity}-custom-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const displayKeys = report?.rows?.length
    ? (selectedFields.length ? selectedFields : Object.keys(report.rows[0]).filter(k => k !== 'id'))
    : [];

  return (
    <Layout>
      <div data-testid="custom-reports-page" className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <ChartLine size={32} weight="fill" className="text-brand" />
            Custom Report Builder
          </h1>
          <p className="text-muted-foreground">Pick an entity, choose fields, filter, export CSV</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Builder column */}
          <div className="bg-card border border-border rounded-lg p-5 space-y-4 lg:col-span-1">
            <div>
              <Label className="overline">Entity</Label>
              <select
                value={entity}
                onChange={(e) => setEntity(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-border font-mono"
                data-testid="custom-report-entity"
              >
                {ENTITY_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>

            <div>
              <Label className="overline">Fields ({selectedFields.length} selected; empty = all)</Label>
              {loadingFields ? (
                <p className="text-sm text-muted-foreground mt-2">Loading...</p>
              ) : (
                <div className="mt-1 max-h-60 overflow-y-auto border border-border p-2 space-y-1">
                  {availableFields.map(f => (
                    <button
                      key={f}
                      onClick={() => toggleField(f)}
                      data-testid={`field-${f}`}
                      className={`w-full text-left px-2 py-1 text-xs font-mono ${selectedFields.includes(f) ? 'bg-brand text-white' : 'hover:bg-muted'}`}
                    >
                      {selectedFields.includes(f) ? '✓ ' : ''}{f}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="overline">Filters</Label>
              <div className="mt-1 space-y-2">
                <Input
                  placeholder="status (e.g., shipped)"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  data-testid="filter-status"
                  className="font-mono text-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    data-testid="filter-start-date"
                    className="font-mono text-sm"
                  />
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    data-testid="filter-end-date"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="overline">Row Limit (max 10000)</Label>
              <Input
                type="number"
                min={1}
                max={10000}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="mt-1 font-mono"
                data-testid="custom-report-limit"
              />
            </div>

            <Button
              onClick={handleRun}
              disabled={running}
              className="w-full bg-brand hover:bg-brand/90"
              data-testid="run-custom-report-btn"
            >
              <Play size={16} className="mr-2" weight="fill" />
              {running ? 'Running...' : 'Run Report'}
            </Button>
          </div>

          {/* Results column */}
          <div className="lg:col-span-2 bg-card border border-border rounded-lg">
            {!report ? (
              <div className="p-12 text-center">
                <Database size={40} className="mx-auto mb-2 text-slate-300" />
                <p className="text-muted-foreground">Configure query on the left and click <span className="font-bold">Run Report</span></p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-bold">
                      {report.returned.toLocaleString()} rows
                      {report.total_matching > report.returned && (
                        <span className="text-muted-foreground font-normal"> of {report.total_matching.toLocaleString()} matching</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">entity: {report.entity}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportCSV}
                    disabled={!report.rows?.length}
                    data-testid="export-csv-btn"
                  >
                    <Download size={14} className="mr-1" /> Export CSV
                  </Button>
                </div>
                <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                  <table className="w-full table-dense">
                    <thead className="sticky top-0 bg-card">
                      <tr>
                        {displayKeys.map(k => <th key={k} className="font-mono text-xs">{k}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.length === 0 ? (
                        <tr><td colSpan={displayKeys.length || 1} className="text-center py-8 text-muted-foreground">No rows match</td></tr>
                      ) : report.rows.map((row, idx) => (
                        <tr key={row.id || idx} data-testid={`report-row-${idx}`}>
                          {displayKeys.map(k => (
                            <td key={k} className="font-mono text-xs text-foreground max-w-xs truncate">
                              {typeof row[k] === 'object' ? JSON.stringify(row[k]) : String(row[k] ?? '-')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
